import { Dispatch, SetStateAction, useState } from "react";
import classNames from "classnames";
import { gql, useQuery } from "@apollo/client";

import Button from "../../Button";
import { Text } from "../../Type";
import styles from "./index.module.scss";
import NotificationBanner from "../../NotificationBanner";
import FileContainer from "./FileContainer";

const GET_CLIENT_FILE = gql`
  query GetClientFile($id: ID!) {
    clientFile(id: $id) {
      id
      status
      client_signature {
        name
        url
      }
      justification {
        name
        url
      }
      letter_of_rationale {
        name
        url
      }
      quote {
        name
        url
      }
      confirmation_of_benefits {
        name
        url
      }
      other_supporting_documents {
        name
        url
      }
      other_supporting_documents_alt {
        name
        url
      }
      ds_signed_form {
        name
        url
      }
      ds_signed_form_original {
        name
        url
      }
      wet_signature {
        name
        url
      }
    }
  }
`;

type PropsType = {
  clientFileId: string | undefined;
  setUploadModalOpen: Dispatch<SetStateAction<boolean>>;
};

function UploadDocumentsModal({ clientFileId, setUploadModalOpen }: PropsType) {
  const [, setBoundaryError] = useState<Error | null>(null);
  const { data, loading, error } = useQuery(GET_CLIENT_FILE, {
    fetchPolicy: "network-only",
    variables: {
      id: clientFileId,
    },
    onError: (error) => {
      setBoundaryError(() => {
        throw error;
      });
    },
  });

  // ToDo
  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error</p>;

  const fileInfo = [
    {
      fieldName: "client_signature",
      file: data.clientFile.client_signature,
      name: "Client Signature",
      description: "Scan of the client’s signature for Section 3 of the ADP form.",
    },
    {
      fieldName: "justification",
      file: data.clientFile.justification,
      name: "Justification for Funding Chart",
      description: "Please upload a justification for funding chart, if required as per the ADP guidelines.",
    },
    {
      fieldName: "letter_of_rationale",
      file: data.clientFile.letter_of_rationale,
      name: "Letter of Rationale",
      description: "If the client is currently or previously a CEP client, a letter of rationale is required to explain why CEP designation needs to be added or removed.",
    },
    {
      fieldName: "quote",
      file: data.clientFile.quote,
      name: "Vendor Quote",
      description: "Please upload a quote for the prescribed device.",
    },
    {
      fieldName: "confirmation_of_benefits",
      file: data.clientFile.confirmation_of_benefits,
      name:"Confirmation of Benefits" ,
      description: "If the client is receiving any social benefits, please upload a confirmation for that."
    },
    {
      fieldName: "wet_signature",
      file: data.clientFile.wet_signature,
      name: "Wet Signature Document",
    },
    {
      fieldName: "other_supporting_documents",
      file: data.clientFile.other_supporting_documents,
      name: "Other Supporting Documents",
      description: "If you have multiple files to upload, please combine them in a single document and upload.",
    },
    {
      fieldName: "other_supporting_documents_alt",
      file: data.clientFile.other_supporting_documents_alt,
      name: "Other Supporting Documents (Alt)",
      description: "If you have multiple files to upload, please combine them in a single document and upload.",
    },
  ];

  return clientFileId ? (
    <>
      <Text size="medium">
        Upload supporting documents for this application.
      </Text>
      <br />
      <div className={classNames(styles.modalGrid5050, styles.bottomBorder)}>
        <Text className={styles.tableHeading} size="medium" weight="bold">
          DOCUMENT
        </Text>
        <Text className={styles.tableHeading} size="medium" weight="bold">
          FILE
        </Text>
      </div>

      {fileInfo.map(({ fieldName, file, name, description }) => (
        <div
          key={fieldName}
          className={classNames(styles.modalGrid5050, styles.tableItems)}
        >
          <div>
            <Text size="medium" weight="bold">
              {name}
            </Text>
            <Text size="medium">{description}</Text>
          </div>
          <div>
            <FileContainer
              file={file}
              fieldName={fieldName}
              clientFileId={clientFileId}
            />
          </div>
        </div>
      ))}

      <div className={styles.horizontalLine100} />
      <div className={styles.modalButton}>
        <Button primary onClick={() => setUploadModalOpen(false)}>
          Done
        </Button>
      </div>
    </>
  ) : (
    <NotificationBanner
      error
      title="Error"
      content="Client file ID unknown"
      className={styles.errorBanner}
    />
  );
}

export default UploadDocumentsModal;
